import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const VisionMissionSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const content = {
    en: {
      vision: {
        title: 'Our Vision',
        description: `To be the leading law firm in Kenya, recognized for our unwavering commitment to legal excellence, innovative solutions, and transformative impact on our clients' lives and businesses.\n\nWe envision a future where legal services are accessible, transparent, and delivered with the highest standards of professionalism, contributing to a just and prosperous society.`
      },
      mission: {
        title: 'Our Mission',description: `To provide exceptional legal services that exceed client expectations through:\n\n• Deep legal expertise and strategic thinking\n• Personalized attention to each client's unique needs\n• Innovative approaches to complex legal challenges\n• Unwavering commitment to integrity and ethics\n• Building long-term partnerships based on trust and results`
      },
      values: [
        {
          icon: 'Shield',
          title: 'Integrity',
          description: 'We uphold the highest ethical standards in all our professional dealings, ensuring transparency and honesty in every interaction.'
        },
        {
          icon: 'Award',
          title: 'Excellence',
          description: 'We strive for excellence in every case, continuously improving our skills and knowledge to deliver superior legal outcomes.'
        },
        {
          icon: 'Users',
          title: 'Client-Centric',
          description: 'Our clients are at the heart of everything we do. We listen, understand, and tailor our services to meet their specific needs.'
        },
        {
          icon: 'Lightbulb',
          title: 'Innovation',
          description: 'We embrace innovative legal strategies and modern technology to provide efficient and effective solutions.'
        }
      ]
    },
    sw: {
      vision: {
        title: 'Maono Yetu',
        description: `Kuwa kampuni ya sheria inayoongoza nchini Kenya, inayotambuliwa kwa kujitolea kwetu bila kikomo kwa ubora wa kisheria, suluhisho za kibunifu, na athari ya kubadilisha maisha ya wateja wetu na biashara zao.\n\nTunaona mustakabali ambapo huduma za kisheria ni za kupatikana, uwazi, na kutolewa kwa viwango vya juu vya utaalamu, vikichangia jamii ya haki na mafanikio.`
      },
      mission: {
        title: 'Dhamira Yetu',
        description: `Kutoa huduma za kisheria za kipekee ambazo zinazidi matarajio ya wateja kupitia:\n\n• Utaalamu wa kina wa kisheria na mawazo ya kimkakati\n• Uangalifu wa kibinafsi kwa mahitaji ya kipekee ya kila mteja\n• Mbinu za kibunifu kwa changamoto ngumu za kisheria\n• Kujitolea bila kikomo kwa uongozi na maadili\n• Kujenga ushirikiano wa muda mrefu kulingana na uaminifu na matokeo`
      },
      values: [
        {
          icon: 'Shield',
          title: 'Uongozi',
          description: 'Tunashikilia viwango vya juu vya kimaadili katika miamala yetu yote ya kitaalamu, tukihakikisha uwazi na uaminifu katika mwingiliano wote.'
        },
        {
          icon: 'Award',
          title: 'Ubora',
          description: 'Tunajitahidi kupata ubora katika kila kesi, tukiendelea kuboresha ujuzi na maarifa yetu kutoa matokeo bora ya kisheria.'
        },
        {
          icon: 'Users',
          title: 'Mteja-Makuu',
          description: 'Wateja wetu ni moyo wa kila kitu tunachofanya. Tunasikiliza, tunaelewa, na tunabadilisha huduma zetu kukidhi mahitaji yao maalum.'
        },
        {
          icon: 'Lightbulb',
          title: 'Ubunifu',
          description: 'Tunakubali mikakati ya kisheria ya kibunifu na teknolojia ya kisasa kutoa suluhisho za ufanisi na za ufanisi.'
        }
      ]
    }
  };

  const currentContent = content[currentLanguage];

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Vision & Mission */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 mb-20">
          {/* Vision */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                <Icon name="Eye" size={24} color="white" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-heading font-bold text-text-primary">
                {currentContent.vision.title}
              </h2>
            </div>
            <div className="prose prose-lg max-w-none">
              {currentContent.vision.description.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-text-secondary leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          {/* Mission */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-accent to-primary rounded-lg flex items-center justify-center">
                <Icon name="Target" size={24} color="white" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-heading font-bold text-text-primary">
                {currentContent.mission.title}
              </h2>
            </div>
            <div className="prose prose-lg max-w-none">
              {currentContent.mission.description.split('\n\n').map((paragraph, index) => (
                <div key={index}>
                  {paragraph.includes('•') ? (
                    <div className="space-y-2">
                      {paragraph.split('\n').map((line, lineIndex) => (
                        <p key={lineIndex} className="text-text-secondary leading-relaxed">
                          {line}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-text-secondary leading-relaxed">
                      {paragraph}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div className="space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary">
              {currentLanguage === 'en' ? 'Our Core Values' : 'Maadili Yetu Makuu'}
            </h2>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              {currentLanguage === 'en' ?'These fundamental principles guide every decision we make and every service we provide.' :'Kanuni hizi za msingi zinaongoza kila uamuzi tunaofanya na kila huduma tunayotoa.'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {currentContent.values.map((value, index) => (
              <div key={index} className="card p-6 text-center space-y-4 hover:shadow-lg transition-shadow duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto">
                  <Icon name={value.icon} size={28} color="white" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-text-primary">
                  {value.title}
                </h3>
                <p className="text-text-secondary leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisionMissionSection;